import { useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';
import type { ManagedEnvironment } from '../../api/types';
import {
  albWafErrorStatus, loadAlbWafConfiguration, loadAlbWafRole,
  saveAlbWafConfiguration, validateAlbWafCatalog, validateAlbWafDraft,
  type AlbWafCatalogEntry, type AlbWafConfiguration, type AlbWafRole,
} from './alb-waf.service';

interface CatalogEditor {
  index: number | null;
  entry: AlbWafCatalogEntry;
}

export function useAlbWafController(apiClient: AxiosInstance, managedEnvironment: ManagedEnvironment) {
  const [snapshot, setSnapshot] = useState<AlbWafConfiguration | null>(null);
  const [draft, setDraft] = useState<AlbWafConfiguration | null>(null);
  const [role, setRole] = useState<AlbWafRole | null>(null);
  const [scope, setScope] = useState<{ client: AxiosInstance; environment: ManagedEnvironment } | null>(null);
  const [loading, setLoading] = useState(true);
  const [configurationMissing, setConfigurationMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [editor, setEditor] = useState<CatalogEditor | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const generation = useRef(0);
  const active = useRef({ apiClient, managedEnvironment });
  active.current = { apiClient, managedEnvironment };

  useEffect(() => {
    const abort = new AbortController();
    const currentGeneration = ++generation.current;
    setLoading(true);
    setSaving(false);
    setConfigurationMissing(false);
    setRole(null);
    setSnapshot(null);
    setDraft(null);
    setEditor(null);
    setBlocked(false);
    setError(null);
    setSuccess(false);
    void (async () => {
      try {
        const confirmedRole = await loadAlbWafRole(apiClient, abort.signal);
        if (abort.signal.aborted || generation.current !== currentGeneration) return;
        setRole(confirmedRole);
        setScope({ client: apiClient, environment: managedEnvironment });
        try {
          const configuration = await loadAlbWafConfiguration(apiClient, abort.signal);
          if (abort.signal.aborted || generation.current !== currentGeneration) return;
          setSnapshot(configuration);
          setDraft(structuredClone(configuration));
        } catch (failure) {
          if (albWafErrorStatus(failure) !== 404) throw failure;
          if (abort.signal.aborted || generation.current !== currentGeneration) return;
          setConfigurationMissing(true);
          if (confirmedRole === 'admin') {
            const initial: AlbWafConfiguration = {
              tag_key: 'waf-policy', revision: '', public_default: '', private_default: '', catalog: [],
            };
            setSnapshot(initial);
            setDraft(structuredClone(initial));
          }
        }
      } catch (failure) {
        if (!abort.signal.aborted && generation.current === currentGeneration) {
          const status = albWafErrorStatus(failure);
          setError(status === 403
            ? 'Acesso negado ao carregar a configura\u00e7\u00e3o (HTTP 403).'
            : status === 502
              ? 'Backend indispon\u00edvel ao consultar a configura\u00e7\u00e3o (HTTP 502).'
              : `N\u00e3o foi poss\u00edvel carregar a configura\u00e7\u00e3o e confirmar o acesso${status ? ` (HTTP ${status})` : ''}.`);
        }
      } finally {
        if (!abort.signal.aborted && generation.current === currentGeneration) setLoading(false);
      }
    })();
    return () => abort.abort();
  }, [apiClient, managedEnvironment, reloadVersion]);

  const current = scope?.client === apiClient && scope.environment === managedEnvironment;
  const dirty = Boolean(current && snapshot && draft && JSON.stringify(snapshot) !== JSON.stringify(draft));
  const validation = draft ? validateAlbWafDraft(draft) : null;
  const canEdit = Boolean(current && role === 'admin' && !loading && !saving && !blocked);

  const changeDefault = (field: 'public_default' | 'private_default', value: string) => {
    if (!canEdit || !draft) return;
    if (!draft.catalog.some((entry) => entry.value === value && entry.approved)) return;
    setDraft({ ...draft, [field]: value });
    setSuccess(false);
  };

  const openEditor = (index: number | null) => {
    if (!canEdit || !draft) return;
    setEditor({ index, entry: index === null
      ? { value: '', description: '', approved: false }
      : { ...draft.catalog[index] } });
    setEditorError(null);
  };

  const applyEditor = () => {
    if (!canEdit || !draft || !editor) return;
    const entries = [...draft.catalog];
    if (editor.index === null) entries.push(editor.entry);
    else entries[editor.index] = editor.entry;
    const changed = { ...draft, catalog: entries };
    const problem = configurationMissing
      ? validateAlbWafCatalog(changed.catalog)
      : validateAlbWafDraft(changed);
    if (problem) {
      setEditorError(problem);
      return;
    }
    setDraft(changed);
    setEditor(null);
    setSuccess(false);
  };

  const removeEntry = (index: number) => {
    if (!canEdit || !draft) return;
    const value = draft.catalog[index]?.value;
    if (value === draft.public_default || value === draft.private_default) return;
    setDraft({ ...draft, catalog: draft.catalog.filter((_, position) => position !== index) });
    setSuccess(false);
  };

  const save = async () => {
    if (!canEdit || !draft || !dirty || validation) return;
    const capturedGeneration = generation.current;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const saved = await saveAlbWafConfiguration(apiClient, draft);
      if (generation.current !== capturedGeneration || active.current.apiClient !== apiClient
        || active.current.managedEnvironment !== managedEnvironment) return;
      setSnapshot(saved);
      setDraft(structuredClone(saved));
      setConfigurationMissing(false);
      setSuccess(true);
    } catch (failure) {
      if (generation.current !== capturedGeneration || active.current.apiClient !== apiClient
        || active.current.managedEnvironment !== managedEnvironment) return;
      const status = albWafErrorStatus(failure);
      if (status === 400) {
        setError('A configura\u00e7\u00e3o foi rejeitada. Revise o cat\u00e1logo e os padr\u00f5es.');
      } else {
        setBlocked(true);
        if (status === 403) setRole('viewer');
        setError(status === 409
          ? 'Conflito de revis\u00e3o. Atualiza\u00e7\u00e3o necess\u00e1ria antes de salvar.'
          : 'Resultado de grava\u00e7\u00e3o n\u00e3o confirmado. Atualiza\u00e7\u00e3o necess\u00e1ria antes de nova tentativa.');
      }
    } finally {
      if (generation.current === capturedGeneration && active.current.apiClient === apiClient
        && active.current.managedEnvironment === managedEnvironment) setSaving(false);
    }
  };

  const discard = () => {
    if (!canEdit || !snapshot) return;
    setDraft(structuredClone(snapshot));
    setEditor(null);
    setError(null);
    setSuccess(false);
  };

  return {
    draft: current ? draft : null, role: current ? role : null, configurationMissing: current && configurationMissing,
    loading: loading || (!current && !error), saving, blocked, error, success,
    dirty, validation, canEdit, canSave: canEdit && dirty && !validation,
    editor, editorError, changeDefault, openEditor, applyEditor, removeEntry, save, discard,
    closeEditor: () => setEditor(null),
    changeEditor: (change: Partial<AlbWafCatalogEntry>) => {
      if (canEdit && editor) setEditor({ ...editor, entry: { ...editor.entry, ...change } });
    },
    reload: () => setReloadVersion((version) => version + 1),
  };
}